import React, { Component } from "react";
import { DEFAULT_PROP, OPACITY_MAP } from "./const";
import "./index.scss";

// —— body 滚动锁：引用计数 + 保存原值 ——
// 原先各实例用「toggle（overflow 当前值取反）」加解锁，隐含假设 body.style.overflow 只由自己控制。
// 一旦弹窗并发/嵌套（如上传超限的 toast 叠加格式/大小 toast），或外部（文件选择等）也改动过 overflow，
// toggle 会把状态判反，导致 toast 关闭后 body 残留 overflow:hidden，页面无法滚动（iOS/Android 均可复现）。
// 改为「进入锁定时保存原值并计数，退出时按计数精确恢复」，保证多弹窗场景下状态始终正确。
let scrollLockCount = 0;
let savedBodyOverflow = "";

function lockBodyScroll() {
  if (scrollLockCount === 0) {
    savedBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  scrollLockCount += 1;
}

function unlockBodyScroll() {
  if (scrollLockCount === 0) return;
  scrollLockCount -= 1;
  if (scrollLockCount === 0) {
    document.body.style.overflow = savedBodyOverflow;
    savedBodyOverflow = "";
  }
}

// 强制复位全局滚动锁：用于 removeAll 一次性关闭所有弹窗的兜底，
// 避免个别实例未走到解锁路径时 body 残留 overflow:hidden。
// 复位后再触发的 unlockBodyScroll 因 count 已为 0 会直接返回，不会把计数改成负值。
export function resetBodyScrollLock() {
  if (scrollLockCount > 0) {
    document.body.style.overflow = savedBodyOverflow;
  }
  scrollLockCount = 0;
  savedBodyOverflow = "";
}

export class MessageBoxEl extends Component<any, any> {
  timer: any;
  // 当前实例是否已持有滚动锁，避免重复 lock/unlock 导致计数不平衡
  locked = false;
  static defaultProps: {
    show: boolean;
    children: null;
    className: string;
    type: string;
    title: string;
    message: string;
    timeout: number;
    opacity: string;
    lockScroll: boolean;
    backgroundFn: boolean;
    formatFn: () => void;
    showConfirmButton: boolean;
    showCancelButton: boolean;
    showCloseIcon: boolean;
    confirmButtonText: string;
    cancelButtonText: string;
  };
  constructor(props) {
    super(props);

    this.timer = null;
    this.state = {
      ...props,
    };

    this.handleAction = this.handleAction.bind(this);
    this.backgroundFunc = this.backgroundFunc.bind(this);
  }

  // 锁定 body 滚动（仅当本实例尚未持锁时）
  lockScrollIfNeeded() {
    if (this.state.lockScroll && !this.locked) {
      this.locked = true;
      lockBodyScroll();
    }
  }

  // 释放 body 滚动（仅当本实例持锁时），保证与 lock 一一对应
  unlockScrollIfNeeded() {
    if (this.locked) {
      this.locked = false;
      unlockBodyScroll();
    }
  }

  // 定时关闭
  timeOut() {
    const { timeout } = this.state;
    // 先清除可能残留的定时器：实例复用 / 重复 open 时避免多个 timer 并存
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (timeout) {
      this.timer = setTimeout(() => {
        this.handleAction("confirm");
      }, timeout);
    }
  }

  // 确定/取消回调
  handleAction(action, data?) {
    const { callback } = this.state;
    this.close(() => {
      this.unlockScrollIfNeeded();
      if (action !== "close") {
        typeof callback === "function" && callback(action, data);
      }
    });
  }

  // 更新弹窗内容
  update(options) {
    this.open();
    this.setState(
      {
        ...options,
      },
      () => {
        this.lockScrollIfNeeded();
        this.timeOut();
      }
    );
  }

  // 打开弹窗
  open(cb?) {
    this.setState(
      {
        show: true,
      },
      () => {
        typeof cb === "function" && cb();
      }
    );
  }

  // 关闭弹窗
  close(cb) {
    // 关闭时清除未触发的定时器：避免 toast 被 removeAll / 手动关闭后，
    // 残留 timer 晚到再次触发 handleAction（空跑）
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.setState(
      {
        show: false,
      },
      () => {
        typeof cb === "function" && cb();
      }
    );
  }

  // 背景弹窗的事件
  backgroundFunc(e) {
    const { backgroundFn } = this.state;
    if (e.target === e.currentTarget && backgroundFn) {
      if (backgroundFn === true) {
        this.handleAction("cancel");
        return;
      }
      const flag = typeof backgroundFn === "function" && backgroundFn();
      if (flag === true) {
        this.handleAction("cancel");
      }
    }
  }

  componentDidMount() {
    this.lockScrollIfNeeded();
    this.timeOut();
  }

  render() {
    const { className, type, show, opacity } = this.state;
    const clsN = "message-box-wrapper " + (className || "");
    const styles = {
      display: show ? "block" : "none",
      background: `rgba(0, 0, 0, ${OPACITY_MAP[opacity]})`,
    };
    return (
      <div onClick={this.backgroundFunc} className={clsN} style={styles}>
        {type === "confirm" && (
          <Confirm {...this.state} handleAction={this.handleAction} />
        )}
        {type === "alert" && (
          <Alert {...this.state} handleAction={this.handleAction} />
        )}
        {type === "toast" && <Toast {...this.state} />}
        {type === "custom" && <Custom {...this.state} instance={this} />}
      </div>
    );
  }
}

// alert组件
export const Alert = (props) => {
  const {
    showConfirmButton,
    handleAction,
    title,
    message,
    confirmButtonText,
    children,
  } = props;
  return (
    <div className="message-box-alert">
      <div className="message-box-header">
        <div className="message-box-title">{title}</div>
      </div>
      <div className="message-box-content">
        <div className="message-box-message">{children || message}</div>
      </div>
      <div className="message-box-btn">
        {showConfirmButton && (
          <div
            className="message-box-confirm-btn"
            onClick={() => handleAction("confirm")}
          >
            <p>{confirmButtonText}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// confirm组件
export const Confirm = (props) => {
  const {
    cancelButtonText,
    showCloseIcon,
    showCancelButton,
    showConfirmButton,
    handleAction,
    title,
    message,
    confirmButtonText,
    children,
  } = props;
  return (
    <div className="message-box-confirm">
      <div className="message-box-header">
        {showCloseIcon && (
          <div
            className="message-box-icon-close"
            onClick={() => handleAction("close")}
          />
        )}
        <div className="message-box-title">{title}</div>
      </div>
      <div className="message-box-content">
        <div className="message-box-message">{children || message}</div>
      </div>
      <div className="message-box-btn">
        {showCancelButton && (
          <div
            className="message-box-cancel-btn"
            onClick={() => handleAction("cancel")}
          >
            <p>{cancelButtonText}</p>
          </div>
        )}
        {showConfirmButton && (
          <div
            className="message-box-confirm-btn"
            onClick={() => handleAction("confirm")}
          >
            <p>{confirmButtonText}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Toast组件
export const Toast = (props) => {
  const { message, children } = props;
  return (
    <div className="message-box-toast">
      <div className="message-box-content">{children || message}</div>
    </div>
  );
};

// Custom自定义组件
export const Custom = (props) => {
  const { message, instance, children } = props;
  return (
    <div className="message-box-custom">
      {children ||
        (typeof message === "function" ? message(instance) : message)}
    </div>
  );
};

MessageBoxEl.defaultProps = {
  ...DEFAULT_PROP,
};
